import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DefaultPalette } from 'office-ui-fabric-react';
import { t } from 'i18next';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { buttonStylesForProjects, buttonStylesMarginX, titleCommandBarButtonStyles } from '../../styles/button-styles';
import { NodoOrganigramaDataGrid } from '../../system/organization_chart/nodo-organigrama-datagrid.interface';
import DataEditor, { GridCell, GridCellKind, GridColumn, Item, EditableGridCell, DataEditorRef } from '@glideapps/glide-data-grid';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { User } from '../../system/User';
import Loading from '../Spinners/Loading';
import { OrganizationChartLevel } from '../../system/organization_chart/OrganizationChartLevel';
import { OPERATORS, TYPES } from '../../common/list-utils';
import {
  CommandBarButton,
  IComboBoxOption,
  Icon,
  IIconProps,
  IStackStyles,
  ITextFieldStyles,
  PrimaryButton,
  Text,
  TextField,
  Toggle,
} from '@fluentui/react';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import '@glideapps/glide-data-grid/dist/index.css';
import { handleColumnResizeUtil } from '../../lib/GlideDataGrid/UtilsGlideGrid';
import ComboBoxSearch from '../Common/ComboBoxSearch';
import { routes } from '../../common/constants';
import { useNavigate } from 'react-router-dom';
import { DetailListPagination } from '../Common/DetailsList/DetailListPagination';

interface ICostCenterProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
}

// --- ICON STYLE ---
const iconClass = mergeStyles({
  position: 'relative',
  top: 2,
  left: 4,
  cursor: 'pointer',
});

const textFieldStyles: Partial<ITextFieldStyles> = {
  field: {
    '::placeholder': {
      color: '#aaa',
    },
  },
  subComponentStyles: {
    label: {
      root: {
        padding: '0px',
      },
    },
  },
};

const stackStyles: Partial<IStackStyles> = { root: { height: 32, fontSize: 12 } };
const FilterClearIcon: IIconProps = { iconName: 'ClearFilter' };

const CostCenterView: React.FC<ICostCenterProps> = (props) => {
  const [data, setData] = useState<NodoOrganigramaDataGrid['data']>([]);
  const [originalData, setOriginalData] = useState<NodoOrganigramaDataGrid['data']>([]);
  const [listLevelsFilters, setListLevelsFilters] = useState<
    {
      quick: boolean;
      name: string;
      type: TYPES;
      placeholder: string;
      operator: OPERATORS;
      contextForCombobox: IComboBoxOption[];
    }[]
  >([]);
  const [filters, setFilters] = useState<{ [index: string]: { key: string; value: string; formatVal: string; placeholder: string } }>({});
  const [levels, setLevels] = useState<OrganizationChartLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const editorRef = useRef<DataEditorRef | null>(null);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const navigate = useNavigate();

  // --- CARGA DE NIVELES ---
  useEffect(() => {
    (async () => {
      props.changeBlockUI(true);
      setLoading(true);
      const levelsResponse = await props.system.getOrganizationChartSystem().getLevels();
      const formattedLevels = await Promise.all(
        levelsResponse.map(async (item) => {
          const organigramaData = await props.system.getOrganizationChartSystem().getLastLevelNodesss(item.getLevel().toString());
          return {
            quick: true,
            name: item.getNameTec(),
            type: TYPES.COMBOBOXTEXT,
            placeholder: t(item.getName()),
            operator: OPERATORS.CONTAINS,
            contextForCombobox: organigramaData.levels,
          };
        }),
      );
      setLevels(levelsResponse);
      setListLevelsFilters(formattedLevels);

      await fetchData();

      props.changeBlockUI(false);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (Object.keys(listLevelsFilters).length > 0) {
      (async () => {
        props.changeBlockUI(true);
        await fetchData();
        props.changeBlockUI(false);
      })();
    }
  }, [filters]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;

    return data.slice(start, end);
  }, [data, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data.length, pageSize]);

  const debouncedCostCenter = useDebouncedCallback(
    // function
    (value) => {
      const newFilters = { ...filters };
      if (value.trim() !== '') {
        newFilters['nor_cecos'] = { key: 'nor_cecos', value: value, formatVal: value, placeholder: t('Centro de costos') };
      } else {
        delete newFilters['nor_cecos'];
      }
      setFilters(newFilters);
    },
    // delay in ms
    1000,
  );

  // --- CARGA DE DATOS ---
  const fetchData = async () => {
    try {
      const orgSystem = props.system.getOrganizationChartSystem();
      // Construcción dinámica de los filtros activos
      const activeFilters = Object.entries(filters).map(([key, { value }]) => {
        const def = listLevelsFilters.find((f) => f.name === key);

        return {
          name: key,
          value: value,
          operator: def?.operator || 'contains',
        };
      });

      const dataGrid = await orgSystem.getDataGrid(1000, activeFilters);
      setData(dataGrid.data);
      setOriginalData(JSON.parse(JSON.stringify(dataGrid.data)));
      setCurrentPage(1);
    } catch (error) {
      console.error({ error });
    }
  };

  // --- COLUMNAS ---
  useEffect(() => {
    const dynamicColumns: GridColumn[] = levels.map((lvl) => ({
      title: t(lvl.getName()),
      id: lvl.getNameTec(),
      themeOverride: {
        headerFontStyle: '900 14px',
      },
      grow: 1,
    }));

    const baseColumns: GridColumn[] = [
      {
        id: 'nor_cecos',
        title: t('Centro de costo'),
        themeOverride: { bgHeaderHovered: '#f4f9ff', headerFontStyle: '900 14px' },
        grow: 1,
      },
      {
        id: 'nor_es_core',
        title: t('Core'),
        themeOverride: { bgHeaderHovered: '#f4f9ff', headerFontStyle: '900 14px' },
        grow: 0,
        width: 80,
      },
    ];

    setColumns([...dynamicColumns, ...baseColumns]);
  }, [levels]);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const column = columns[col];
      const item = paginatedData[row];

      if (!item || !column) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
          readonly: true,
        };
      }

      const value = item[column.id as keyof typeof item];

      // CHECKBOX para nor_es_core
      if (column.id === 'nor_es_core') {
        return {
          kind: GridCellKind.Boolean,
          data: String(value) === '1' || value === 1,
          allowOverlay: false,
          readonly: false,
        };
      }

      if (column.id === 'nor_cecos') {
        // Editable solo nor_cecos
        const isEditable = column.id === 'nor_cecos';

        return {
          kind: GridCellKind.Text,
          data: String(value ?? ''),
          displayData: String(value ?? ''),
          allowOverlay: isEditable,
          readonly: !isEditable,
        };
      }

      return {
        kind: GridCellKind.Text,
        data: String(value ?? ''),
        displayData: String(value ?? ''),
        allowOverlay: false,
        readonly: true,
      };
    },
    [paginatedData, columns],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const column = columns[col];

      if (!column || row >= paginatedData.length) return;

      const realRowIndex = (currentPage - 1) * pageSize + row;
      const copy = [...data];
      const item = { ...copy[realRowIndex] };

      // guardar como 1 o 0
      if (column.id === 'nor_es_core' && newValue.kind === GridCellKind.Boolean) {
        item[column.id] = newValue.data ? 1 : 0;
      }

      // Texto normal
      if (column.id === 'nor_cecos' && newValue.kind === GridCellKind.Text) {
        item[column.id] = String(newValue.data);
      }

      copy[realRowIndex] = item;

      setData(copy);
    },
    [columns, data, paginatedData, currentPage, pageSize],
  );

  const validateCell = useCallback((cell: Item, newValue: EditableGridCell) => {
    if (newValue.kind === GridCellKind.Text) {
      return /^[0-9]*$/.test(String(newValue.data));
    }
    return true;
  }, []);

  // --- GUARDAR ---
  const handleSave = useCallback(async () => {
    try {
      props.changeBlockUI(true);
      const payload = data
        .filter((current, index) => {
          const original = originalData[index];
          if (!original) return true;
          const oldValue = (original.nor_cecos ?? '').trim();
          const newValue = (current.nor_cecos ?? '').trim();
          const oldCore = Number(original.nor_es_core ?? 0);
          const newCore = Number(current.nor_es_core ?? 0);
          return oldValue !== newValue || oldCore !== newCore;
        })
        .map((d) => ({
          nor_id: d.nor_id,
          nor_cecos: d.nor_cecos?.trim() === '' ? null : d.nor_cecos,
          nor_es_core: d.nor_es_core,
        }));

      if (payload.length === 0) {
        props.courier.messageInfo(t('No hay cambios para guardar'));
        return;
      }

      const orgSystem = props.system.getOrganizationChartSystem();
      const response = await orgSystem.saveCostCenters(payload);

      if (response.success) {
        props.changeBlockUI(true);
        await fetchData();
        props.changeBlockUI(false);
        props.courier.messageSuccess(t('Los cambios se guardaron de manera exitosa'));
      } else {
        props.courier.messageError(`Algunos registros fallaron: ${response.errors.join(', ')}`);
      }
    } catch (error) {
      console.error(error);
      props.courier.messageError(t('Error al guardar los cambios'));
    } finally {
      props.changeBlockUI(false);
    }
  }, [data, originalData]);

  const hasChanges = useMemo(() => {
    if (!data.length || !originalData.length) return false;

    return data.some((current, index) => {
      const original = originalData[index];
      if (!original) return true;

      const oldValue = (original.nor_cecos ?? '').trim();
      const newValue = (current.nor_cecos ?? '').trim();

      const oldCore = Number(original.nor_es_core ?? 0);
      const newCore = Number(current.nor_es_core ?? 0);

      return oldValue !== newValue || oldCore !== newCore;
    });
  }, [data, originalData]);

  // --- MENÚ ---
  const itemsMenu = useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Cecos y Core'),
        iconProps: { iconName: 'Backlog', color: DefaultPalette.themeLighterAlt },
        buttonStyles: titleCommandBarButtonStyles,
      },
      {
        key: 'add',
        text: t('Nuevo'),
        iconProps: { iconName: 'Add', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesMarginX,
        onClick: () => navigate(`${routes.cost_center}/create`),
      },
      {
        key: 'save',
        text: t('Guardar'),
        iconProps: { iconName: 'Save', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesForProjects,
        onClick: () => void handleSave(),
        disabled: loading || !hasChanges,
      },
    ],
    [handleSave, navigate, loading, hasChanges],
  );

  // --- ELIMINAR FILTRO DESDE CHIP ---
  const removeFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    if (filterKey === 'nor_cecos') {
      setSearchValue('');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchValue('');
  };

  const getOptionByValues = (opt: any, context: any): string[] => {
    if (!opt || !context) return [''];
    const found = context.find((o: any) => o.key === opt.key && o.text === opt.value);
    return found ? [found.key] : [''];
  };

  const handleColumnResize = useCallback(
    handleColumnResizeUtil((cols) => setColumns(cols)),
    [],
  );
  if (loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar title="" items={itemsMenu} withBorderBottom>
      <div className="padding-list">
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row row-flex-calendar">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl2 ms-xxl2 filter-menu" dir="ltr">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 0 8px 0' }}>
                <Text block variant="medium" className="ms-hiddenXlUp">
                  {t('Filtros')}
                </Text>

                <CommandBarButton
                  onClick={clearFilters}
                  className="bg-transparent"
                  allowDisabledFocus
                  styles={stackStyles}
                  iconProps={FilterClearIcon}
                  text={t('Limpiar')}
                />
              </div>

              {/* FILTROS */}
              {listLevelsFilters.length > 0 && (
                <div className="ms-Grid-row" dir="ltr">
                  <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12">
                    <div className="checkbox c-checkbox">
                      <label className="mb-1" style={{ width: 'fit-content' }}>
                        <Toggle
                          label={t('Core')}
                          checked={filters['nor_es_core'] ? true : false}
                          onClick={() => {
                            const newFilters = { ...filters };
                            if (filters['nor_es_core']) {
                              delete newFilters['nor_es_core'];
                            } else {
                              newFilters['nor_es_core'] = { key: 'nor_es_core', value: '1', formatVal: 'Sí', placeholder: t('Core') };
                            }
                            setFilters(newFilters);
                          }}
                          className={'filter-textfield-list'}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12">
                    <TextField
                      className="filter-textfield-list"
                      label={t('Centro de costos')}
                      value={searchValue}
                      onChange={(e, value) => {
                        setSearchValue(value || '');
                        debouncedCostCenter(value || '');
                      }}
                      styles={textFieldStyles}
                    />
                  </div>

                  {listLevelsFilters.map((filterConfig) => (
                    <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12">
                      <label className="ms-Label label-filter">{filterConfig.placeholder}</label>
                      <ComboBoxSearch
                        key={filterConfig.name}
                        options={filterConfig.contextForCombobox}
                        className="combo-box-filter-detaillist"
                        selected={getOptionByValues(filters[filterConfig.name], filterConfig.contextForCombobox)}
                        onSelectOption={(option) => {
                          const newFilters = { ...filters };
                          if (option)
                            newFilters[filterConfig.name] = {
                              key: option.key.toString(),
                              value: option.text,
                              formatVal: option.text,
                              placeholder: filterConfig.placeholder,
                            };
                          else delete newFilters[filterConfig.name];
                          setFilters(newFilters);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl10 ms-xxl10 container-data-grid-cost-center"
              style={{ height: '70vh', width: '100%' }}
            >
              <Text block variant="large" className="text-bold title-filter-wrapper mb-2">
                {t('Cecos y Core')}

                {/* TAGS DE FILTROS APLICADOS */}
                {Object.entries(filters).map(([key, { formatVal, placeholder }]) => {
                  const label = placeholder;
                  return (
                    <div key={key} className="chip-filter">
                      <label className="chip-filter-label">
                        {label}: {formatVal}
                        <Icon iconName="Cancel" className={iconClass} onClick={() => removeFilter(key)} />
                      </label>
                    </div>
                  );
                })}
              </Text>

              {data.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 50, gap: 20 }}>
                  <Icon iconName="Search" styles={{ root: { fontSize: 38, color: '#ccc' } }} />
                  <Text variant="mediumPlus" styles={{ root: { color: '#666' } }}>
                    {t('No se encontraron resultados')}
                  </Text>
                </div>
              ) : (
                <>
                  <DataEditor
                    width="100%"
                    ref={editorRef}
                    columns={columns}
                    maxColumnWidth={1000}
                    height={"70vh"}
                    getCellContent={getCellContent}
                    onCellEdited={onCellEdited}
                    validateCell={validateCell}
                    rows={paginatedData.length}
                    rowMarkers="none"
                    smoothScrollX
                    smoothScrollY
                    rowHeight={35}
                    onColumnResize={handleColumnResize}
                    keybindings={{
                      activateCell: false,
                      copy: false,
                      paste: false,
                      delete: false,
                      selectAll: false,
                    }}
                    editOnType={true}
                  />

                  <div className="mb-2">
                    <DetailListPagination
                      page={currentPage}
                      perPage={pageSize}
                      totalRows={data.length}
                      onChangePerPage={(e, item) => {
                        setPageSize(Number(item.key));
                        setCurrentPage(1);
                      }}
                      onChangePage={(action) => {
                        switch (action) {
                          case 'toFirst':
                            setCurrentPage(1);
                            break;

                          case 'back':
                            setCurrentPage((prev) => Math.max(prev - 1, 1));
                            break;

                          case 'next':
                            setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                            break;

                          case 'toLast':
                            setCurrentPage(totalPages);
                            break;
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
};

export default CostCenterView;
